import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

import { TranslateModule } from '@ngx-translate/core';

import { TSqChipsModule } from '@tsq-web/chips';
import { FeatureFlagEnabledDirective } from '@tsq-web/feature-flag';
import { TSqFormsModule } from '@tsq-web/forms';
import { TSqGenericComponentsModule } from '@tsq-web/generic-components';

import { ChipsResponse } from '../../models/chips-response.model';
import { ChipsGroupComponent } from '../chips-group/chips-group.component';

@Component({
  selector: 'comm-rating-details-step',
  templateUrl: './rating-details-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TSqChipsModule,
    TSqFormsModule,
    TSqGenericComponentsModule,
    ReactiveFormsModule,
    ChipsGroupComponent,
    FeatureFlagEnabledDirective,
  ],
})
export class RatingDetailsStepComponent {
  @Input() positiveChips: ChipsResponse;
  @Input() negativeChips: ChipsResponse;
  @Input() reasonControl: FormControl<string>;

  /**
   * @deprecated
   * Replaced by `positiveChips` and `negativeChips`.
   */
  @Input() suggestionChips: string[];
  /**
   * @deprecated
   * Replaced by `managementSelectedIndexes` and `platformSelectedIndexes`.
   */
  @Input() selectedChipsIndexes: number[];

  @Output() readonly chipSelected = new EventEmitter<string[]>();
  @Output() readonly feedbackSubmitted = new EventEmitter<void>();

  /**
   * @deprecated
   * Replaced by `chipSelected`.
   */
  @Output() suggestionSelected = new EventEmitter<number>();

  protected managementSelectedIndexes: number[] = [];
  protected platformSelectedIndexes: number[] = [];

  private _isPositiveFeedback: boolean;

  @Input() set isPositiveFeedback(value: boolean) {
    this._isPositiveFeedback = value;
    this.resetSelectedChips();
  }

  get isPositiveFeedback(): boolean {
    return this._isPositiveFeedback;
  }

  get isReasonMandatory(): boolean {
    return this.reasonControl.hasValidator(Validators.required);
  }

  get hasChipsSelected(): boolean {
    return (
      !!this.managementSelectedIndexes.length ||
      !!this.platformSelectedIndexes.length ||
      !!this.selectedChipsIndexes.length
    );
  }

  protected onSelectedChange(index: number, source: 'management' | 'platform'): void {
    if (source === 'management') {
      this.managementSelectedIndexes = this.toggleItemInArray(
        this.managementSelectedIndexes,
        index,
      );
    } else if (source === 'platform') {
      this.platformSelectedIndexes = this.toggleItemInArray(this.platformSelectedIndexes, index);
    }

    this.chipSelected.emit(this.buildChipTextArrFromSelectedIndexes());
  }

  private toggleItemInArray(sourceArr: number[], target: number): number[] {
    const exists = sourceArr.includes(target);
    return exists ? sourceArr.filter(existing => existing !== target) : [...sourceArr, target];
  }

  private buildChipTextArrFromSelectedIndexes(): string[] {
    const chips = this.isPositiveFeedback ? this.positiveChips : this.negativeChips;

    return [
      ...this.managementSelectedIndexes.map(index => chips?.management[index]),
      ...this.platformSelectedIndexes.map(index => chips?.platform[index]),
    ];
  }

  private resetSelectedChips(): void {
    this.managementSelectedIndexes = [];
    this.platformSelectedIndexes = [];
  }
}
